"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import type { DefaultValues, UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItemWithoutDiv,
  UnstyledFormLabel,
} from "@/components/ui/form";

import {
  CurrencyInput,
  HeadingLabelCell,
  LabelCell,
  NumberDisplayCell,
  PercentageDisplayCell,
  sum,
  Table,
  TableHeader,
  Tr,
} from "./shared";

const formSchema = z.object({
  netSellingPrice: z.number().positive(),
  productCosts: z.number().positive(),
  freightCost: z.number().positive(),
  fulfillmentCost: z.number().positive(),
  transactionFees: z.number().positive(),
  adSpend: z.number().positive(),
  fixedOperatingCosts: z.number().positive(),
  unitsSold: z.number().positive(),
});

const defaultValues = {
  netSellingPrice: 100,
  productCosts: 12,
  freightCost: 1,
  fulfillmentCost: 5,
  transactionFees: 1,
  adSpend: 46,
  fixedOperatingCosts: 100_000,
  unitsSold: 4_000,
} satisfies DefaultValues<z.infer<typeof formSchema>>;

interface ContentProps {
  form: UseFormReturn<z.infer<typeof formSchema>>;
  breakevenPoint: number;
  contributionMarginPerUnit: number;
  contributionMarginPercent: number;
  grossProfit: number;
  operatingProfit: number;
  variableCosts: number;
  t: ReturnType<typeof useTranslations<"Calculators">>;
  tCommon: ReturnType<typeof useTranslations<"Common">>;
}

export function ContributionMargin() {
  const t = useTranslations("Calculators");
  const tCommon = useTranslations("Common");
  const form = useForm<z.infer<typeof formSchema>>({
    mode: "onChange",
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const variableCosts = sum(
    form.watch([
      "productCosts",
      "freightCost",
      "fulfillmentCost",
      "transactionFees",
    ])
  );

  const grossProfit = form.watch("netSellingPrice") - variableCosts;
  const contributionMarginPerUnit = grossProfit - form.watch("adSpend");
  const contributionMarginPercent =
    (contributionMarginPerUnit / form.watch("netSellingPrice")) * 100;
  const breakevenPoint = Math.ceil(
    form.watch("fixedOperatingCosts") / contributionMarginPerUnit
  );
  const operatingProfit =
    form.watch("unitsSold") * contributionMarginPerUnit -
    form.watch("fixedOperatingCosts");

  const tableProps = {
    form,
    breakevenPoint,
    contributionMarginPerUnit,
    contributionMarginPercent,
    grossProfit,
    operatingProfit,
    variableCosts,
    t,
    tCommon,
  } satisfies ContentProps;

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <TableHeader>
          <p className="text-body-md text-text-on-dark sticky top-0 font-semibold">
            {t("contributionMargin")}
          </p>
        </TableHeader>
        <DesktopTable {...tableProps} />
        <MobileTable {...tableProps} />
      </form>
    </Form>
  );
}

function MobileTable({
  form,
  breakevenPoint,
  contributionMarginPerUnit,
  contributionMarginPercent,
  grossProfit,
  operatingProfit,
  variableCosts,
  t,
  tCommon,
}: ContentProps) {
  const colSpan = 2;
  return (
    <Table className="md:hidden">
      <colgroup>
        <col className="w-1/2" />
        <col className="w-1/2" />
      </colgroup>
      <tbody className="bg-surface">
        <Tr>
          <HeadingLabelCell colSpan={colSpan}>
            {t("unitLevel")}
          </HeadingLabelCell>
        </Tr>
        <FormField
          control={form.control}
          name="netSellingPrice"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("netSellingPrice")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <Tr>
          <LabelCell colSpan={colSpan} className="italic">
            {t("minusVariableCosts")}
          </LabelCell>
        </Tr>

        <FormField
          control={form.control}
          name="productCosts"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("productCosts")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />
        <FormField
          control={form.control}
          name="freightCost"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("freightCost")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <FormField
          control={form.control}
          name="fulfillmentCost"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("fulfillmentCost")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <FormField
          control={form.control}
          name="transactionFees"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr className="border-b-0">
                <LabelCell>
                  <UnstyledFormLabel>{t("transactionFees")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <CurrencyInput {...field} />
                    </FormControl>
                    {variableCosts}
                  </div>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <Tr className="border-b-0">
          <LabelCell className="bg-pulse-50 rounded-l-lg font-bold">
            {t("grossProfit")}
          </LabelCell>
          <NumberDisplayCell className="bg-pulse-50 rounded-r-lg text-end font-bold">
            {grossProfit}
          </NumberDisplayCell>
        </Tr>

        <FormField
          control={form.control}
          name="adSpend"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr className="border-b-0">
                <LabelCell>
                  <UnstyledFormLabel>{t("adSpend")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <Tr>
          <LabelCell className="bg-pulse-50 rounded-tl-lg font-bold">
            {t("contributionMarginPerUnit")}
          </LabelCell>
          <NumberDisplayCell className="bg-pulse-50 rounded-tr-lg font-bold">
            {contributionMarginPerUnit}
          </NumberDisplayCell>
        </Tr>

        <Tr className="border-b-0">
          <LabelCell className="bg-pulse-50 rounded-bl-lg font-bold">
            %
          </LabelCell>
          <PercentageDisplayCell className="bg-pulse-50 rounded-br-lg font-bold">
            {contributionMarginPercent}
          </PercentageDisplayCell>
        </Tr>
        <Tr>
          <HeadingLabelCell colSpan={colSpan}>
            {t("companyLevel")}
          </HeadingLabelCell>
        </Tr>
        <FormField
          control={form.control}
          name="fixedOperatingCosts"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr className="border-b">
                <LabelCell>
                  <UnstyledFormLabel>
                    {t("fixedOperatingCosts")}
                  </UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <Tr>
          <LabelCell colSpan={colSpan}>{t("breakEvenPointInUnits")}</LabelCell>
        </Tr>
        <Tr>
          <td />
          <NumberDisplayCell>{breakevenPoint}</NumberDisplayCell>
        </Tr>
        <FormField
          control={form.control}
          name="unitsSold"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr className="border-b-0">
                <LabelCell>
                  <UnstyledFormLabel>{t("unitsSold")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />
        <Tr className="border-b-0 bg-blue-800">
          <LabelCell className="text-pulse-700 rounded-l-lg bg-blue-800">
            {t("operatingProfit")}
          </LabelCell>
          <NumberDisplayCell className="text-pulse-700 rounded-r-lg bg-blue-800">
            {operatingProfit}
          </NumberDisplayCell>
        </Tr>
      </tbody>
      <tfoot className="py-4">
        <Tr className="border-x-0 border-b-0">
          <td colSpan={2} className="px-5 py-3 text-center">
            <button
              type="submit"
              className="hidden"
              onClick={(e) => e.preventDefault()}
            />
            <Button
              onClick={(e) => {
                e.preventDefault();
                Object.entries(defaultValues).forEach(([key, value]) => {
                  form.setValue(key as keyof typeof defaultValues, value);
                });
              }}
              variant="link"
              size="sm"
              className="text-body"
            >
              {tCommon("clearResults")}
            </Button>
          </td>
        </Tr>
      </tfoot>
    </Table>
  );
}

function DesktopTable({
  form,
  breakevenPoint,
  contributionMarginPerUnit,
  contributionMarginPercent,
  grossProfit,
  operatingProfit,
  variableCosts,
  t,
  tCommon,
}: ContentProps) {
  return (
    <Table className="hidden md:table">
      <colgroup>
        <col className="w-8/12" />
        <col className="w-2/12" />
        <col className="w-2/12" />
      </colgroup>
      <tbody className="bg-surface">
        <Tr>
          <HeadingLabelCell>{t("unitLevel")}</HeadingLabelCell>
          <NumberDisplayCell />
          <NumberDisplayCell />
        </Tr>
        <FormField
          control={form.control}
          name="netSellingPrice"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("netSellingPrice")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell />
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <Tr>
          <LabelCell className="italic" colSpan={3}>
            {t("minusVariableCosts")}
          </LabelCell>
        </Tr>

        <FormField
          control={form.control}
          name="productCosts"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("productCosts")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <NumberDisplayCell />
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <FormField
          control={form.control}
          name="freightCost"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("freightCost")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <NumberDisplayCell />
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <FormField
          control={form.control}
          name="fulfillmentCost"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("fulfillmentCost")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <NumberDisplayCell />
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <FormField
          control={form.control}
          name="transactionFees"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr className="border-b-0">
                <LabelCell>
                  <UnstyledFormLabel>{t("transactionFees")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <NumberDisplayCell>{variableCosts}</NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <Tr className="border-b-0">
          <LabelCell className="bg-pulse-50 rounded-l-lg font-bold">
            {t("grossProfit")}
          </LabelCell>
          <NumberDisplayCell className="bg-pulse-50" />
          <NumberDisplayCell className="bg-pulse-50 rounded-r-lg font-bold">
            {grossProfit}
          </NumberDisplayCell>
        </Tr>

        <FormField
          control={form.control}
          name="adSpend"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr className="border-b-0">
                <LabelCell>
                  <UnstyledFormLabel>{t("adSpend")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell />
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <Tr>
          <LabelCell className="bg-pulse-50 rounded-tl-lg font-bold">
            {t("contributionMarginPerUnit")}
          </LabelCell>
          <NumberDisplayCell className="bg-pulse-50" />
          <NumberDisplayCell className="bg-pulse-50 rounded-tr-lg font-bold">
            {contributionMarginPerUnit}
          </NumberDisplayCell>
        </Tr>

        <Tr className="border-b-0">
          <LabelCell className="bg-pulse-50 rounded-bl-lg font-bold">
            %
          </LabelCell>
          <NumberDisplayCell className="bg-pulse-50" />
          <PercentageDisplayCell className="bg-pulse-50 rounded-br-lg font-bold">
            {contributionMarginPercent}
          </PercentageDisplayCell>
        </Tr>

        <Tr>
          <HeadingLabelCell>{t("companyLevel")}</HeadingLabelCell>
          <NumberDisplayCell />
          <NumberDisplayCell />
        </Tr>

        <FormField
          control={form.control}
          name="fixedOperatingCosts"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>
                    {t("fixedOperatingCosts")}
                  </UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell />
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <Tr>
          <LabelCell>{t("breakEvenPointInUnits")}</LabelCell>
          <NumberDisplayCell />
          <NumberDisplayCell>{breakevenPoint}</NumberDisplayCell>
        </Tr>

        <FormField
          control={form.control}
          name="unitsSold"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr className="border-b-0">
                <LabelCell>
                  <UnstyledFormLabel>{t("unitsSold")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell />
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <Tr className="">
          <LabelCell className="bg-pulse-50 rounded-tl-lg font-bold">
            {t("operatingProfit")}
          </LabelCell>
          <NumberDisplayCell className="bg-pulse-50" />
          <NumberDisplayCell className="bg-pulse-50 rounded-tr-lg font-bold">
            {operatingProfit}
          </NumberDisplayCell>
        </Tr>
      </tbody>
      <tfoot className="py-4">
        <Tr className="border-x-0 border-b-0 md:border-x-0">
          <td colSpan={3} className="px-5 py-3 text-center">
            <button
              type="submit"
              className="hidden"
              onClick={(e) => e.preventDefault()}
            />
            <Button
              onClick={() => {
                Object.entries(defaultValues).forEach(([key, value]) => {
                  form.setValue(key as keyof typeof defaultValues, value);
                });
              }}
              variant="link"
              size="sm"
              className="text-body"
            >
              {tCommon("clearResults")}
            </Button>
          </td>
        </Tr>
      </tfoot>
    </Table>
  );
}
